import { createContext, useContext, useState, useRef } from 'react';

// Create the context
const TripContext = createContext();

// Create a custom hook so the itinerary components can access data w/out the risk of 'undefined' error crash
export const useTripContext = () => {
    const context = useContext(TripContext);
    if (!context) {
        throw new Error("useTripContext must be used within a TripProvider");
    }
    return context;
};

// Create the Provider component which will wrap our page
export const TripProvider = ({ children }) => {

    // Trip meta state
    const [title, setTitle] = useState('');
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [fromDateObj, setFromDateObj] = useState(null);
    const [toDateObj, setToDateObj] = useState(null);

    // Itinerary data state
    const [itineraryDays, setItineraryDays] = useState([]);
    const [defaultColumns, setDefaultColumns] = useState([
        { id: 'time', label: 'Time', width: 130, type: 'time' },
        { id: 'place', label: 'Place', width: 220, type: 'text', placeholder: 'E.g. Fushimi Inari Shrine' },
        {
            id: 'category',
            label: 'Category',
            width: 215,
            type: 'tags',
            tags: ['Accomodation', 'Food & Drinks', 'Shopping', 'Transport', 'Leisure', 'Miscellaneous']
        },
        { id: 'price', label: 'Price', width: 105, type: 'number', placeholder: 'E.g. 400' },
        { id: 'payer', label: 'Payer', width: 110, type: 'person', placeholder: 'E.g. Me' },
        { id: 'notes', label: 'Notes', width: 245, type: 'text', placeholder: 'E.g. Bring jacket!' },
    ]);
    const [tripMembers, setTripMembers] = useState([]);

    // Trip-level "structure version" — bumps when a column is deleted / type-changed
    const [structureVersion, setStructureVersion] = useState(1);

    // Whether the current user may edit this trip (owner/editor) vs view-only (viewer)
    // Defaults true so create mode is editable before any trip is loaded.
    const [canEdit, setCanEdit] = useState(true);

    // Unsaved changes tracker
    const [isDirty, setIsDirty] = useState(false);

    // Reload hook
    const reloadRef = useRef(null);
    const reloadTrip = () => { if (reloadRef.current) return reloadRef.current(); };

    // Count the duration in days (null when not valid)
    const [durationInDays, setDurationInDays] = useState(null);

    // Bundle everything we want to share into the "value" object
    const value = {
        title, setTitle,
        selectedCountries, setSelectedCountries,
        fromDateObj, setFromDateObj,
        toDateObj, setToDateObj,
        durationInDays, setDurationInDays,
        itineraryDays, setItineraryDays,
        defaultColumns, setDefaultColumns,
        tripMembers, setTripMembers,
        structureVersion, setStructureVersion,
        canEdit, setCanEdit,
        reloadRef, reloadTrip,
        isDirty, setIsDirty
    };

    return (
        <TripContext.Provider value={value}>
            {children}
        </TripContext.Provider>
    );
};
